/**
 * Presence container — writes the 30s heartbeat and subscribes to co-viewers,
 * rendering the presentational {@link PresenceLayer}. Coverage-excluded (SDK
 * wiring); the layer itself and `initialsOf` carry the tests.
 */
/* c8 ignore start */
import { useEffect, useState } from 'react';
import { useAppStore } from '../state/store.js';
import { watchPresence, writePresenceHeartbeat, deletePresenceDoc } from '../firebase/atlasApi.js';
import type { PresenceViewer } from '../state/atlasApiTypes.js';
import { PresenceLayer } from './PresenceLayer.js';

const HEARTBEAT_MS = 30_000;

export function PresenceContainer({ entityId }: { entityId: string }): React.JSX.Element | null {
  const { user } = useAppStore();
  const [viewers, setViewers] = useState<PresenceViewer[]>([]);

  useEffect(() => {
    if (!user) return;
    void writePresenceHeartbeat(entityId, user);
    const interval = setInterval(() => void writePresenceHeartbeat(entityId, user), HEARTBEAT_MS);
    const unsub = watchPresence(entityId, setViewers);

    // Best-effort eager cleanup on tab close — reduces ghost-viewer window from
    // 90s (scheduled sweep) to near-instant for clean navigations.
    const onUnload = (): void => { void deletePresenceDoc(entityId, user.uid); };
    window.addEventListener('beforeunload', onUnload);

    return () => {
      clearInterval(interval);
      unsub();
      window.removeEventListener('beforeunload', onUnload);
      void deletePresenceDoc(entityId, user.uid);
    };
  }, [entityId, user]);

  if (!user) return null;
  return <PresenceLayer viewers={viewers} selfUid={user.uid} />;
}
/* c8 ignore stop */
